import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, KeyRound, Info } from "lucide-react";
import { ExcluirCadastroBtn } from "@/components/ExcluirCadastroBtn";

export default function Empresas() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [acesso, setAcesso] = useState({ email: "", senha: "", existente: null });

  const load = () => api.get("/empresas").then(r => setList(r.data));
  useEffect(() => { load(); }, []);

  const [siglaTocada, setSiglaTocada] = useState(false);
  useEffect(() => {
    if (siglaTocada) return;
    const base = (form.nome_fantasia || form.razao_social || "").toUpperCase();
    if (!base) return;
    const stop = new Set(["E", "DE", "DA", "DO", "DAS", "DOS", "LTDA", "LTDA.", "ME", "EPP", "S/A", "SA", "EIRELI", "&"]);
    const palavras = base.split(/[\s\-_/.]+/).filter(p => p && !stop.has(p));
    const sigla = palavras.slice(0, 3).map(p => p[0]).join("");
    setForm(f => ({ ...f, sigla_os: sigla.replace(/[^A-Z0-9]/g, "") }));
  }, [form.nome_fantasia, form.razao_social, siglaTocada]);

  const abrirNovo = () => {
    setEditingId(null);
    setForm(emptyForm());
    setAcesso({ email: "", senha: "", existente: null });
    setSiglaTocada(false);
    setOpen(true);
  };

  const abrirEditar = async (e) => {
    setEditingId(e.id);
    setForm({ ...emptyForm(), ...e, sigla_os: e.sigla_os || "" });
    setSiglaTocada(true);
    setOpen(true);
    try {
      const r = await api.get(`/empresas/${e.id}/acesso`);
      setAcesso({ email: r.data.email || "", senha: "", existente: r.data.tem_acesso ? r.data.email : null });
    } catch { setAcesso({ email: "", senha: "", existente: null }); }
  };

  const submit = async () => {
    try {
      const body = {
        nome: form.razao_social || form.nome,
        razao_social: form.razao_social,
        nome_fantasia: form.nome_fantasia,
        cnpj: form.cnpj,
        status_cnpj: form.status_cnpj,
        sigla_os: (form.sigla_os || "").toUpperCase().replace(/\s+/g, ""),
        endereco: form.endereco, bairro: form.bairro, cidade: form.cidade, estado: form.estado,
        telefone_comercial: form.telefone_comercial, telefone_financeiro: form.telefone_financeiro,
        telefone_operacional: form.telefone_operacional, telefone_posto: form.telefone_posto,
        email_administrativo: form.email_administrativo || null,
        email_financeiro: form.email_financeiro || null,
        responsavel_legal: form.responsavel_legal,
        responsavel_operacional: form.responsavel_operacional,
        responsavel_financeiro: form.responsavel_financeiro,
      };
      let empresaId = editingId;
      if (editingId) {
        await api.put(`/empresas/${editingId}`, body);
      } else {
        const r = await api.post("/empresas", body);
        empresaId = r.data.id;
      }
      // Acesso da empresa (opcional)
      if (acesso.email && acesso.senha) {
        await api.post(`/empresas/${empresaId}/acesso`, { email: acesso.email, password: acesso.senha });
        toast.success("Empresa salva · acesso de login configurado");
      } else {
        toast.success(editingId ? "Empresa atualizada" : "Empresa cadastrada");
      }
      setOpen(false);
      setEditingId(null);
      setForm(emptyForm());
      setSiglaTocada(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-end gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">MontaJá · Rede</div>
          <h1 className="font-display font-extrabold text-3xl text-aj-blue-deep mt-1">Empresas</h1>
          <p className="text-sm text-aj-text-muted mt-1">
            Cadastro essencial das empresas contratantes. Nichos, áreas de atuação e valores são configurados pela própria empresa ao logar.
          </p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setSiglaTocada(false); } }}>
          <DialogTrigger asChild>
            <button onClick={abrirNovo} className="btn-aj-primary text-sm" data-testid="btn-nova-empresa">
              <Plus size={16} /> Nova empresa
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display">{editingId ? "Editar empresa" : "Nova empresa"}</DialogTitle>
              <DialogDescription>
                Cadastro essencial (identificação, endereço, contatos, responsáveis e acesso de login).
                A empresa define nichos, áreas e tabela de valores no próprio painel.
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="identificacao">
              <TabsList className="rounded-lg">
                <TabsTrigger value="identificacao" data-testid="tab-id">Identificação</TabsTrigger>
                <TabsTrigger value="endereco" data-testid="tab-end">Endereço</TabsTrigger>
                <TabsTrigger value="contatos" data-testid="tab-cont">Contatos</TabsTrigger>
                <TabsTrigger value="responsaveis" data-testid="tab-resp">Responsáveis</TabsTrigger>
                <TabsTrigger value="acesso" data-testid="tab-acesso">Acesso</TabsTrigger>
              </TabsList>

              <TabsContent value="identificacao" className="space-y-4 mt-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <FF label="Razão social" v={form.razao_social} on={(v) => setForm(f => ({...f, razao_social: v}))} testid="emp-razao-social" />
                  <FF label="Nome fantasia" v={form.nome_fantasia} on={(v) => setForm(f => ({...f, nome_fantasia: v}))} testid="emp-nome-fantasia" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <FF label="CNPJ" v={form.cnpj} on={(v) => setForm(f => ({...f, cnpj: v}))} testid="emp-cnpj" />
                  <div>
                    <Label className="text-[11px] font-bold uppercase tracking-wider text-aj-text-muted">Status do CNPJ</Label>
                    <Select value={form.status_cnpj} onValueChange={(v) => setForm(f => ({...f, status_cnpj: v}))}>
                      <SelectTrigger className="mt-2 rounded-lg" data-testid="emp-status-cnpj"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ativo">Ativo</SelectItem>
                        <SelectItem value="inativo">Inativo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="rounded-lg border border-aj-amber bg-aj-amber-soft p-4">
                  <Label className="text-[11px] font-bold uppercase tracking-wider text-aj-orange-hover">Sigla da OS (numeração automática dos atendimentos)</Label>
                  <div className="flex items-center gap-3 mt-2">
                    <Input
                      value={form.sigla_os || ""}
                      onChange={(e) => { setSiglaTocada(true); setForm(f => ({...f, sigla_os: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6) })); }}
                      placeholder="Ex.: EM"
                      className="font-mono w-32 text-center text-lg font-bold rounded-lg bg-white"
                      maxLength={6}
                      data-testid="emp-sigla-os"
                    />
                    <div className="text-xs text-aj-text-muted">
                      Exemplo de OS gerada: <span className="font-mono font-bold">{form.sigla_os || "EMP"}-{new Date().getFullYear()}-00001</span><br/>
                      <span className="text-[10px]">Sugerida automaticamente a partir do nome. Você pode editar.</span>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="endereco" className="space-y-4 mt-4">
                <FF label="Endereço completo (rua, número, complemento)" v={form.endereco} on={(v) => setForm(f => ({...f, endereco: v}))} testid="emp-end" />
                <div className="grid md:grid-cols-3 gap-4">
                  <FF label="Bairro" v={form.bairro} on={(v) => setForm(f => ({...f, bairro: v}))} testid="emp-bairro" />
                  <FF label="Cidade" v={form.cidade} on={(v) => setForm(f => ({...f, cidade: v}))} testid="emp-cidade" />
                  <FF label="Estado" v={form.estado} on={(v) => setForm(f => ({...f, estado: v}))} testid="emp-estado" />
                </div>
              </TabsContent>

              <TabsContent value="contatos" className="space-y-4 mt-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <FF label="Telefone comercial" v={form.telefone_comercial} on={(v) => setForm(f => ({...f, telefone_comercial: v}))} testid="emp-tel-com" />
                  <FF label="Telefone financeiro" v={form.telefone_financeiro} on={(v) => setForm(f => ({...f, telefone_financeiro: v}))} testid="emp-tel-fin" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <FF label="Telefone operacional" v={form.telefone_operacional} on={(v) => setForm(f => ({...f, telefone_operacional: v}))} testid="emp-tel-op" />
                  <FF label="Telefone do posto / responsável pela montagem" v={form.telefone_posto} on={(v) => setForm(f => ({...f, telefone_posto: v}))} testid="emp-tel-posto" />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <FF label="E-mail administrativo" v={form.email_administrativo} on={(v) => setForm(f => ({...f, email_administrativo: v}))} testid="emp-email-adm" />
                  <FF label="E-mail financeiro" v={form.email_financeiro} on={(v) => setForm(f => ({...f, email_financeiro: v}))} testid="emp-email-fin" />
                </div>
              </TabsContent>

              <TabsContent value="responsaveis" className="space-y-4 mt-4">
                <FF label="Responsável legal" v={form.responsavel_legal} on={(v) => setForm(f => ({...f, responsavel_legal: v}))} testid="emp-resp-legal" />
                <FF label="Responsável operacional" v={form.responsavel_operacional} on={(v) => setForm(f => ({...f, responsavel_operacional: v}))} testid="emp-resp-op" />
                <FF label="Responsável financeiro" v={form.responsavel_financeiro} on={(v) => setForm(f => ({...f, responsavel_financeiro: v}))} testid="emp-resp-fin" />
              </TabsContent>

              <TabsContent value="acesso" className="space-y-4 mt-4">
                <div className="rounded-lg border border-aj-blue bg-blue-50 p-4 flex gap-3 text-sm text-aj-blue-deep">
                  <KeyRound size={18} className="flex-shrink-0 mt-0.5" />
                  <div>
                    {acesso.existente
                      ? <>Esta empresa já possui acesso com o e-mail <strong>{acesso.existente}</strong>. Preencha abaixo apenas para alterar e-mail ou redefinir a senha.</>
                      : <>Crie o login que a empresa usará para entrar no próprio painel (importar planilhas, definir valores, nichos e áreas).</>}
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <FF label="E-mail de login" v={acesso.email} on={(v) => setAcesso(a => ({...a, email: v}))} testid="emp-acesso-email" />
                  <div>
                    <Label className="text-[11px] font-bold uppercase tracking-wider text-aj-text-muted">Senha (mín. 6 caracteres)</Label>
                    <Input type="password" value={acesso.senha} onChange={(e) => setAcesso(a => ({...a, senha: e.target.value}))}
                      className="mt-2 rounded-lg" data-testid="emp-acesso-senha" />
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <div className="rounded-lg bg-aj-surface border border-aj-border p-3 flex gap-2 text-xs text-aj-text-muted mt-2">
              <Info size={14} className="flex-shrink-0 mt-0.5 text-aj-orange" />
              Nichos, áreas de atuação e valores pagos aos montadores são definidos pela própria empresa ao logar
              (páginas "Minha Empresa" e "Tabela de Valores" com regra D+60).
            </div>

            <button onClick={submit} className="btn-aj-primary w-full justify-center mt-3" data-testid="emp-salvar">
              {editingId ? "Atualizar empresa" : "Salvar empresa"}
            </button>
          </DialogContent>
        </Dialog>
      </div>

      <div className="aj-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-[#E2E8F0] bg-aj-surface-2">
            <tr>
              <Th>Razão social</Th><Th>Nome fantasia</Th><Th>Sigla OS</Th><Th>CNPJ</Th><Th>Cidade</Th><Th>Ações</Th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-aj-text-muted">Nenhuma empresa cadastrada</td></tr>}
            {list.map((e) => (
              <tr key={e.id} className="border-b border-[#E2E8F0] last:border-0 hover:bg-aj-surface transition-colors" data-testid={`empresa-row-${e.id}`}>
                <Td><strong>{e.razao_social || e.nome}</strong>{e.status_cnpj === "inativo" && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-aj-danger text-white">CNPJ inativo</span>}</Td>
                <Td>{e.nome_fantasia || "—"}</Td>
                <Td className="font-mono text-xs font-bold text-aj-blue-dark">{e.sigla_os || "—"}</Td>
                <Td className="font-mono text-xs">{e.cnpj}</Td>
                <Td>{e.cidade || "—"}</Td>
                <Td>
                  <div className="flex items-center gap-3">
                    <button onClick={() => abrirEditar(e)} className="text-xs font-semibold text-aj-orange hover:underline" data-testid={`emp-editar-${e.id}`}>Editar</button>
                    <ExcluirCadastroBtn endpoint={`/admin/empresas/${e.id}`} nome={e.razao_social || e.nome}
                      onExcluido={load} testid={`emp-excluir-${e.id}`} />
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function emptyForm() {
  return {
    razao_social: "", nome_fantasia: "", nome: "", cnpj: "", status_cnpj: "ativo",
    sigla_os: "",
    endereco: "", bairro: "", cidade: "", estado: "",
    telefone_comercial: "", telefone_financeiro: "", telefone_operacional: "", telefone_posto: "",
    email_administrativo: "", email_financeiro: "",
    responsavel_legal: "", responsavel_operacional: "", responsavel_financeiro: "",
  };
}
function FF({ label, v, on, testid }) {
  return (
    <div>
      <Label className="text-[11px] font-bold uppercase tracking-wider text-aj-text-muted">{label}</Label>
      <Input value={v || ""} onChange={(e) => on(e.target.value)} className="mt-2 rounded-lg" data-testid={testid} />
    </div>
  );
}
function Th({ children }) { return <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">{children}</th>; }
function Td({ children, className="" }) { return <td className={`p-3 ${className}`}>{children}</td>; }
